import { z } from 'zod';
import { AccountPersonaDisclosureSchema } from '../account-persona/disclosure.js';
import { CardSchema } from '../conversation-ui/cards.js';
import { StateRefSchema } from '../state-machines.js';
import { isJsonRecord, parseStrictJsonRecord } from '../strict-json.js';

export const CONVERSATION_INTERACTION_PROTOCOL_VERSION = 1 as const;

const FieldOptionSchema = z.object({
  value: z.string().min(1),
  label: z.string().min(1),
  description: z.string().optional(),
});

const FieldBaseSchema = z.object({
  id: z.string().min(1),
  label: z.string().min(1),
  helpText: z.string().optional(),
  required: z.boolean().optional().default(false),
});

const TextFieldSchema = FieldBaseSchema.extend({
  kind: z.enum(['text', 'textarea', 'rich_text', 'url', 'email', 'code']),
  placeholder: z.string().optional(),
  defaultValue: z.string().optional(),
  minLength: z.number().int().nonnegative().optional(),
  maxLength: z.number().int().positive().optional(),
});

const NumberFieldSchema = FieldBaseSchema.extend({
  kind: z.enum(['number', 'currency', 'percent', 'slider']),
  defaultValue: z.number().optional(),
  min: z.number().optional(),
  max: z.number().optional(),
  step: z.number().positive().optional(),
});

const BooleanFieldSchema = FieldBaseSchema.extend({
  kind: z.literal('boolean'),
  defaultValue: z.boolean().optional(),
});

const ChoiceFieldSchema = FieldBaseSchema.extend({
  kind: z.enum(['single_choice', 'multi_choice', 'select', 'radio', 'checkboxes']),
  options: z.array(FieldOptionSchema).min(1),
  defaultValue: z.union([z.string(), z.array(z.string())]).optional(),
});

const ComboboxFieldSchema = FieldBaseSchema.extend({
  kind: z.literal('combobox'),
  options: z.array(FieldOptionSchema).optional().default([]),
  placeholder: z.string().optional(),
  allowCustomValue: z.boolean().optional().default(false),
});

const DateFieldSchema = FieldBaseSchema.extend({
  kind: z.enum(['date', 'time', 'date_range']),
  defaultValue: z.union([z.string(), z.object({ start: z.string(), end: z.string() })]).optional(),
});

const EntityPickerFieldSchema = FieldBaseSchema.extend({
  kind: z.enum(['entity_picker', 'artifact_picker', 'file_picker']),
  entityTypes: z.array(z.string().min(1)).optional(),
  multiple: z.boolean().optional().default(false),
});

const StructuredFieldSchema = FieldBaseSchema.extend({
  kind: z.enum(['key_value', 'repeater', 'editable_table']),
  columns: z
    .array(
      z.object({
        id: z.string().min(1),
        label: z.string().min(1),
        kind: z.enum(['text', 'number', 'boolean', 'select']).optional().default('text'),
        options: z.array(FieldOptionSchema).optional(),
      }),
    )
    .optional(),
  minItems: z.number().int().nonnegative().optional(),
  maxItems: z.number().int().positive().optional(),
});

export const ConversationInteractionFieldSchema = z.discriminatedUnion('kind', [
  TextFieldSchema,
  NumberFieldSchema,
  BooleanFieldSchema,
  ChoiceFieldSchema,
  ComboboxFieldSchema,
  DateFieldSchema,
  EntityPickerFieldSchema,
  StructuredFieldSchema,
]);

export const ConversationInteractionActionSchema = z.object({
  id: z.string().min(1),
  label: z.string().min(1),
  kind: z.enum([
    'submit',
    'cancel',
    'decline',
    'approve',
    'reject',
    'edit',
    'url',
    'create_task',
    'create_project',
    'create_project_thread',
    'create_project_change',
    'create_workstream',
    'start_workflow',
    'apply_proposal',
  ]),
  style: z.enum(['primary', 'secondary', 'danger', 'warning']).optional().default('secondary'),
  payload: z.record(z.string(), z.unknown()).optional(),
});

export const ConversationInteractionRequestSchema = z.object({
  type: z.literal('conversation.interaction_request'),
  version: z.literal(CONVERSATION_INTERACTION_PROTOCOL_VERSION),
  interactionKey: z.string().min(1),
  title: z.string().min(1),
  body: z.string().optional(),
  targetKind: z.enum(['agent', 'workflow', 'task', 'project', 'workstream']).optional(),
  expectedOutput: z.string().optional(),
  fields: z.array(ConversationInteractionFieldSchema).default([]),
  actions: z
    .array(ConversationInteractionActionSchema)
    .optional()
    .default([{ id: 'submit', label: 'Submit', kind: 'submit', style: 'primary' }]),
  submitLabel: z.string().optional().default('Submit'),
  why: z.string().optional(),
});

export const ConversationInteractionSubmissionSchema = z.object({
  interactionId: z.string().min(1),
  sourceMessageId: z.string().min(1),
  action: ConversationInteractionActionSchema.shape.kind.default('submit'),
  values: z.record(z.string(), z.unknown()).default({}),
  displayText: z.string().min(1).max(8000),
  idempotencyKey: z.string().min(1).max(200),
});

export const TextPartSchema = z.object({
  type: z.literal('text'),
  content: z.string().min(1),
});

export const SectionPartSchema = z.object({
  type: z.literal('section'),
  title: z.string().min(1),
  body: z.string().optional(),
  items: z
    .array(
      z.object({
        label: z.string().min(1),
        value: z.string().min(1),
        emphasis: z.enum(['normal', 'strong', 'muted', 'positive', 'warning', 'danger']).optional().default('normal'),
      }),
    )
    .optional()
    .default([]),
});

export const TablePartSchema = z.object({
  type: z.literal('table'),
  title: z.string().min(1).optional(),
  columns: z
    .array(
      z.object({
        id: z.string().min(1),
        label: z.string().min(1),
        align: z.enum(['left', 'center', 'right']).optional().default('left'),
      }),
    )
    .min(1),
  rows: z.array(z.record(z.string(), z.union([z.string(), z.number(), z.boolean(), z.null()]))).default([]),
});

export const ChecklistPartSchema = z.object({
  type: z.literal('checklist'),
  title: z.string().min(1).optional(),
  items: z
    .array(
      z.object({
        label: z.string().min(1),
        detail: z.string().optional(),
        status: z.enum(['pending', 'running', 'complete', 'blocked', 'failed']).optional().default('pending'),
      }),
    )
    .min(1),
});

export const ProposalPartSchema = z.object({
  type: z.literal('proposal'),
  title: z.string().min(1),
  summary: z.string().optional(),
  rationale: z.string().optional(),
  steps: z
    .array(
      z.object({
        title: z.string().min(1),
        detail: z.string().optional(),
        output: z.string().optional(),
      }),
    )
    .default([]),
});

export const InteractionRequestPartSchema = z.object({
  type: z.literal('interaction_request'),
  interactionId: z.string().optional(),
  status: z.enum(['open', 'submitted', 'cancelled', 'expired', 'superseded']).default('open'),
  request: ConversationInteractionRequestSchema,
  stateRef: StateRefSchema.optional(),
});

export const ArtifactRefPartSchema = z.object({
  type: z.literal('artifact_ref'),
  artifactId: z.union([z.string(), z.number()]),
  kind: z.string().min(1),
  title: z.string().min(1),
  summary: z.string().optional(),
  stateRef: StateRefSchema.optional(),
});

export const ProgressPartSchema = z.object({
  type: z.literal('progress'),
  label: z.string().min(1),
  status: z.enum(['pending', 'running', 'complete', 'blocked', 'failed']),
  detail: z.string().optional(),
  stateRef: StateRefSchema.optional(),
});

export const WarningPartSchema = z.object({
  type: z.literal('warning'),
  severity: z.enum(['info', 'warning', 'error']),
  message: z.string().min(1),
  recommendation: z.string().optional(),
  stateRef: StateRefSchema.optional(),
});

/** Simplified landing: a typed card produced by an entity-skill tool (never by the LLM). */
export const CardPartSchema = z.object({
  type: z.literal('card'),
  card: CardSchema,
  stateRef: StateRefSchema.optional(),
});

/** Simplified landing: plain-language log of what the agent did this turn ("◦ what I did behind the scenes"). */
export const BehindTheScenesPartSchema = z.object({
  type: z.literal('behind_the_scenes'),
  items: z.array(z.string().min(1).max(500)).min(1).max(40),
});

/** Simplified landing: follow-up chips the agent offers; each sends `message` back into the conversation. */
export const SuggestionsPartSchema = z.object({
  type: z.literal('suggestions'),
  items: z
    .array(z.object({ label: z.string().min(1).max(80), message: z.string().min(1).max(500) }))
    .min(1)
    .max(6),
});

/**
 * Account persona (account-persona 04): the message is an AI simulation's answer — the client shows the disclosure as
 * the message's footer (EU AI Act Art. 50(1)), and that the version is unevaluated while it is.
 */
export const DisclosurePartSchema = z.object({
  type: z.literal('disclosure'),
  disclosure: AccountPersonaDisclosureSchema,
  /** The persona that answered. */
  personaName: z.string().min(1).max(200),
});

/**
 * An answer's proposal of another persona to lead the conversation (the `agent-suggestion` output contract,
 * conversation-members spec). The engine resolves the id in the organization before storing it, so the name is the
 * persona's own; the person acts on it — asks it, or (the owner) makes it the lead. The agent never switches itself, and
 * a model cannot write this part directly (it is not an assistant-turn part).
 */
export const AgentSuggestionPartSchema = z.object({
  type: z.literal('agent_suggestion'),
  /** The persona's entity id (`agent_template_list`). */
  personaId: z.number().int().positive(),
  name: z.string().min(1).max(200),
  reason: z.string().min(1).max(500),
});

const ASSISTANT_TURN_PARTS = [
  TextPartSchema,
  SectionPartSchema,
  TablePartSchema,
  ChecklistPartSchema,
  ProposalPartSchema,
  InteractionRequestPartSchema,
  ArtifactRefPartSchema,
  ProgressPartSchema,
  WarningPartSchema,
  CardPartSchema,
  BehindTheScenesPartSchema,
  SuggestionsPartSchema,
  DisclosurePartSchema,
] as const;

/** The parts a model's answer may hold (`ConversationAssistantTurnSchema`). */
export const AssistantTurnPartSchema = z.discriminatedUnion('type', [...ASSISTANT_TURN_PARTS]);

/**
 * The parts a stored message holds. Who wrote a message is not a part: it is the message's author
 * (`conversation_messages.author_member_id`), which a model never chooses.
 */
export const ConversationMessagePartSchema = z.discriminatedUnion('type', [
  ...ASSISTANT_TURN_PARTS,
  AgentSuggestionPartSchema,
]);

export const ConversationAssistantTurnSchema = z.object({
  type: z.literal('conversation.assistant_turn'),
  version: z.literal(CONVERSATION_INTERACTION_PROTOCOL_VERSION),
  message: z.string().optional(),
  parts: z.array(AssistantTurnPartSchema).optional().default([]),
  interactions: z.array(ConversationInteractionRequestSchema).optional().default([]),
});

export type ConversationInteractionField = z.infer<typeof ConversationInteractionFieldSchema>;
export type ConversationInteractionAction = z.infer<typeof ConversationInteractionActionSchema>;
export type ConversationInteractionRequest = z.infer<typeof ConversationInteractionRequestSchema>;
export type ConversationInteractionSubmission = z.infer<typeof ConversationInteractionSubmissionSchema>;
export type ConversationMessagePart = z.infer<typeof ConversationMessagePartSchema>;
export type ConversationAssistantTurn = z.infer<typeof ConversationAssistantTurnSchema>;

export interface ConversationInteractionDefinition {
  key: string;
  version: 1;
  title: string;
  description: string;
  capabilityTags: string[];
  renderer: 'form' | 'review' | 'artifact' | 'status';
}

export const CONVERSATION_INTERACTION_REGISTRY: ConversationInteractionDefinition[] = [
  {
    key: 'conversation.form',
    version: 1,
    title: 'Form',
    description: 'Collect one or more structured answers before resuming the agent.',
    capabilityTags: ['intake', 'disambiguation', 'clarification'],
    renderer: 'form',
  },
  {
    key: 'conversation.choice',
    version: 1,
    title: 'Choice',
    description: 'Collect a single or multi-choice selection with explicit submit.',
    capabilityTags: ['choice', 'routing'],
    renderer: 'form',
  },
  {
    key: 'conversation.review',
    version: 1,
    title: 'Review',
    description: 'Ask the user to review, edit, approve, reject, or cancel a proposal.',
    capabilityTags: ['review', 'approval'],
    renderer: 'review',
  },
  {
    key: 'conversation.plan_selection',
    version: 1,
    title: 'Plan Selection',
    description: 'Present a plan and collect the next step the user wants to authorize.',
    capabilityTags: ['planning', 'workflow'],
    renderer: 'review',
  },
  {
    key: 'conversation.artifact_selection',
    version: 1,
    title: 'Artifact Selection',
    description: 'Ask the user to choose files, artifacts, or entities to use as context.',
    capabilityTags: ['artifact', 'context'],
    renderer: 'form',
  },
];

export function getConversationInteractionDefinition(key: string): ConversationInteractionDefinition | null {
  return CONVERSATION_INTERACTION_REGISTRY.find((definition) => definition.key === key) ?? null;
}

type JsonSchemaRecord = Record<string, unknown>;

function asJsonSchemaRecord(value: unknown): JsonSchemaRecord | null {
  return value && typeof value === 'object' && !Array.isArray(value) ? (value as JsonSchemaRecord) : null;
}

function strictenJsonSchemaObjects(value: unknown): unknown {
  if (Array.isArray(value)) {
    return value.map(strictenJsonSchemaObjects);
  }

  const record = asJsonSchemaRecord(value);
  if (!record) return value;

  const copy: JsonSchemaRecord = {};
  for (const [key, nested] of Object.entries(record)) {
    if (key === '$schema') continue;
    copy[key] = strictenJsonSchemaObjects(nested);
  }

  if (copy.type === 'object' && asJsonSchemaRecord(copy.properties) && copy.additionalProperties === undefined) {
    copy.additionalProperties = false;
  }

  return copy;
}

function toStrictProviderJsonSchema(schema: z.ZodType): JsonSchemaRecord {
  return strictenJsonSchemaObjects(z.toJSONSchema(schema, { io: 'input' })) as JsonSchemaRecord;
}

function withRegisteredInteractionKeys(schema: JsonSchemaRecord): JsonSchemaRecord {
  const properties = asJsonSchemaRecord(schema.properties);
  const interactionKey = asJsonSchemaRecord(properties?.interactionKey);
  if (interactionKey) {
    interactionKey.enum = CONVERSATION_INTERACTION_REGISTRY.map((definition) => definition.key);
  }
  return schema;
}

export function getConversationInteractionRequestJsonSchema(): JsonSchemaRecord {
  return withRegisteredInteractionKeys(toStrictProviderJsonSchema(ConversationInteractionRequestSchema));
}

export function getConversationAssistantTurnJsonSchema(): JsonSchemaRecord {
  const schema = toStrictProviderJsonSchema(ConversationAssistantTurnSchema);
  const properties = asJsonSchemaRecord(schema.properties);
  const parts = asJsonSchemaRecord(properties?.parts);
  const interactions = asJsonSchemaRecord(properties?.interactions);
  const required = Array.isArray(schema.required) ? schema.required : [];

  if (asJsonSchemaRecord(properties?.message)) {
    (properties!.message as JsonSchemaRecord).maxLength = 4000;
  }
  if (parts) {
    parts.minItems = 1;
    parts.maxItems = 20;
  }
  if (interactions) {
    interactions.maxItems = 1;
    const requestSchema = asJsonSchemaRecord(interactions.items);
    if (requestSchema) withRegisteredInteractionKeys(requestSchema);
  }

  schema.required = [...new Set([...required, 'parts'])];

  return schema;
}

export function validateConversationInteractionRequest(input: unknown): ConversationInteractionRequest {
  const request = ConversationInteractionRequestSchema.parse(input);
  if (!getConversationInteractionDefinition(request.interactionKey)) {
    throw new Error(`Unknown conversation interaction: ${request.interactionKey}`);
  }
  if (request.actions.length === 0) {
    request.actions = [{ id: 'submit', label: request.submitLabel, kind: 'submit', style: 'primary' }];
  }
  return request;
}

export function validateConversationInteractionSubmission(
  request: ConversationInteractionRequest,
  input: unknown,
): ConversationInteractionSubmission {
  const submission = ConversationInteractionSubmissionSchema.parse(input);
  const allowedActions = new Set(request.actions.map((action) => action.kind));
  if (!allowedActions.has(submission.action)) {
    throw new Error(`Action "${submission.action}" is not allowed for this interaction.`);
  }
  if (submission.action === 'cancel' || submission.action === 'decline' || submission.action === 'reject') {
    return submission;
  }

  const allowedFieldIds = new Set(request.fields.map((field) => field.id));
  for (const key of Object.keys(submission.values)) {
    if (!allowedFieldIds.has(key)) {
      throw new Error(`Unexpected field: ${key}`);
    }
  }

  for (const field of request.fields) {
    const value = submission.values[field.id];
    if (
      field.required &&
      (value === undefined || value === null || value === '' || (Array.isArray(value) && value.length === 0))
    ) {
      throw new Error(`Missing required field: ${field.label}`);
    }

    if (
      (field.kind === 'multi_choice' || field.kind === 'checkboxes') &&
      value !== undefined &&
      !Array.isArray(value)
    ) {
      throw new Error(`Field "${field.label}" must be an array.`);
    }

    if (
      (field.kind === 'single_choice' ||
        field.kind === 'multi_choice' ||
        field.kind === 'select' ||
        field.kind === 'radio' ||
        field.kind === 'checkboxes') &&
      value !== undefined &&
      value !== ''
    ) {
      const allowed = new Set(field.options.map((option) => option.value));
      const submittedValues = Array.isArray(value) ? value.map(String) : [String(value)];
      const invalid = submittedValues.find((item) => !allowed.has(item));
      if (invalid) throw new Error(`Invalid option for "${field.label}": ${invalid}`);
    }

    if (field.kind === 'combobox' && field.allowCustomValue !== true && value !== undefined && value !== '') {
      const allowed = new Set((field.options ?? []).map((option) => option.value));
      const submittedValues = Array.isArray(value) ? value.map(String) : [String(value)];
      const invalid = submittedValues.find((item) => !allowed.has(item));
      if (invalid) throw new Error(`Invalid option for "${field.label}": ${invalid}`);
    }

    if (
      (field.kind === 'text' ||
        field.kind === 'textarea' ||
        field.kind === 'rich_text' ||
        field.kind === 'url' ||
        field.kind === 'email' ||
        field.kind === 'code') &&
      typeof value === 'string'
    ) {
      if (field.minLength !== undefined && value.length < field.minLength) {
        throw new Error(`Field "${field.label}" is shorter than ${field.minLength} characters.`);
      }
      if (field.maxLength !== undefined && value.length > field.maxLength) {
        throw new Error(`Field "${field.label}" is longer than ${field.maxLength} characters.`);
      }
    }

    if (
      (field.kind === 'number' || field.kind === 'currency' || field.kind === 'percent' || field.kind === 'slider') &&
      value !== undefined &&
      value !== ''
    ) {
      if (typeof value !== 'number' || Number.isNaN(value)) throw new Error(`Field "${field.label}" must be a number.`);
      if (field.min !== undefined && value < field.min)
        throw new Error(`Field "${field.label}" is below ${field.min}.`);
      if (field.max !== undefined && value > field.max)
        throw new Error(`Field "${field.label}" is above ${field.max}.`);
    }
  }

  return submission;
}

export function buildInteractionDisplayText(
  request: ConversationInteractionRequest,
  values: Record<string, unknown>,
): string {
  const lines = [request.title];
  for (const field of request.fields) {
    const value = values[field.id];
    if (value === undefined || value === null || value === '') continue;
    const options = 'options' in field ? (field.options ?? []) : [];
    const optionLabel = (raw: unknown) => {
      const stringValue = String(raw);
      return options.find((option) => option.value === stringValue)?.label ?? stringValue;
    };
    lines.push(`${field.label}: ${Array.isArray(value) ? value.map(optionLabel).join(', ') : optionLabel(value)}`);
  }
  return lines.join('\n');
}

export function getEmitInteractionToolDefinition(): {
  name: string;
  description: string;
  inputSchema: Record<string, unknown>;
} {
  return {
    name: 'emit_interaction_request',
    description:
      'Ask the user for structured input. The UI will render the fields and send one explicit structured submission after the user clicks Submit.',
    inputSchema: getConversationInteractionRequestJsonSchema(),
  };
}

export function getConversationInteractionProtocolPrompt(): string {
  const registry = CONVERSATION_INTERACTION_REGISTRY.map(
    (definition) => `- ${definition.key}: ${definition.description}`,
  ).join('\n');

  return `Conversation Interaction Protocol v1 is available.

Registered interaction families:
${registry}

Rules:
- Use normal prose when no user input is needed.
- Use structured display parts for readable business content: section for captured facts, table for comparisons, checklist for status, proposal for plans.
- Every structured turn must include at least one renderable part. For plain text, use a text part. For approvals and forms, use an interaction_request part.
- When the user must answer choices, fill fields, approve/reject, select entities, or authorize a next step, emit an interaction_request part instead of prose-only UI instructions.
- For providers with tools, call the tool named emit_interaction_request.
- For text-only providers, return exactly one JSON object matching conversation.assistant_turn with a non-empty parts array.
- The user interface will render the registered interaction and collect every field with one explicit Submit action.
- Do not use markdown tables or numbered option menus for content that should be structured, compared, selected, routed, validated, or acted on.
- Entity-specific update protocols must return their own exact JSON object when the active entity assistant instructions require them.
- Do not use markdown headings, bold-label paragraphs, or bullet lists for captured context, constraints, candidate options, comparisons, statuses, or plans. Use section/table/checklist/proposal parts.
- Markdown is allowed only for short emphasis inside non-actionable prose, not as the source of truth for decisions, forms, comparisons, options, statuses, or plans.
- Do not assume clicking an option sends an answer; the UI submits only after the user clicks Submit.`;
}

function invalidStructuredTurn(): ConversationAssistantTurn {
  return {
    type: 'conversation.assistant_turn',
    version: 1,
    message: 'I hit a response formatting issue. Please send your last message again.',
    parts: [
      {
        type: 'warning',
        severity: 'warning',
        message: 'The assistant returned an unsupported structured response.',
        recommendation: 'Retry the message. If it repeats, the active template needs protocol tuning.',
      },
    ],
    interactions: [],
  };
}

function isConversationProtocolType(value: unknown): boolean {
  return (
    value === 'conversation.assistant_turn' ||
    value === 'conversation.interaction_request' ||
    value === 'conversation.ui_blocks'
  );
}

function hasUnsupportedNestedConversationPayload(value: Record<string, unknown>): boolean {
  const output = isJsonRecord(value.output) ? value.output : null;
  const response = isJsonRecord(value.response) ? value.response : null;

  return Boolean(
    (output && (output.ui_blocks !== undefined || isConversationProtocolType(output.type))) ||
      (response && isConversationProtocolType(response.type)),
  );
}

function isGenericAgentResponsePayload(value: Record<string, unknown>): boolean {
  return typeof value.status === 'string' && isJsonRecord(value.output) && typeof value.confidence === 'number';
}

export function parseAssistantInteractionTurn(raw: string): ConversationAssistantTurn | null {
  const parsed = parseStrictJsonRecord(raw);
  if (!parsed) return null;

  const structured = ConversationAssistantTurnSchema.safeParse(parsed);
  if (structured.success) {
    const hasRenderablePayload =
      (structured.data.parts?.length ?? 0) > 0 || (structured.data.interactions?.length ?? 0) > 0;
    return hasRenderablePayload ? structured.data : invalidStructuredTurn();
  }

  const request = ConversationInteractionRequestSchema.safeParse(parsed);
  if (request.success) {
    return {
      type: 'conversation.assistant_turn',
      version: 1,
      parts: [{ type: 'interaction_request', status: 'open', request: request.data }],
      interactions: [],
    };
  }

  if (
    isConversationProtocolType(parsed.type) ||
    hasUnsupportedNestedConversationPayload(parsed) ||
    isGenericAgentResponsePayload(parsed)
  ) {
    return invalidStructuredTurn();
  }

  return null;
}

export function materializeAssistantTurn(raw: string): {
  content: string;
  parts: ConversationMessagePart[];
} {
  const turn = parseAssistantInteractionTurn(raw);
  if (!turn) {
    return { content: raw, parts: [] };
  }

  const parts = [...(turn.parts ?? [])];
  for (const interaction of turn.interactions ?? []) {
    parts.push({ type: 'interaction_request', status: 'open', request: interaction });
  }

  const textParts = parts.filter((part): part is z.infer<typeof TextPartSchema> => part.type === 'text');
  const content = turn.message ?? textParts.map((part) => part.content).join('\n\n') ?? '';
  return {
    content: content.trim() || 'I need a bit more information.',
    parts,
  };
}
